import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Observable, interval } from 'rxjs';
import { map, catchError, retry, switchMap, startWith } from 'rxjs/operators';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';
import { APP_CONFIG } from '@cfg/env/app.config';
import {
  mapMetricsResponse,
  buildMetricsQueryParams,
  buildObservationsQueryParams
} from './observations-query.utils';
import {
  ObservationListItemDTO,
  ReprocessHistoryDTO,
  ObservationsQueryParams,
  AddHistoryRequest,
  ObservationMetricsResponse,
  ObservationMetricsParams
} from '../api/observations-api.interface';
import { ObservationsPaginatedResult } from './observations.service';
import { ObservationMetrics } from '@model/observation/observation-metrics.model';

interface LaboratoryObservationsResponse {
  observations: ObservationListItemDTO[];
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

interface UpdateStatusRequest {
  statusId: string;
}

/**
 * Service for observations in Laboratory module.
 * Calls the 1249-sig-laboratorio-ms backend.
 * HU50-922997: Gestión de Observaciones
 */
@Injectable({
  providedIn: 'root'
})
export class ObservationsLaboratoryService {
  private readonly httpClient = inject(HttpClient);
  private readonly errorHandler = inject(HttpErrorHandlerService);
  private readonly appConfig = inject(APP_CONFIG);

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  private readonly BASE_ENDPOINT = 'observations';

  private buildBaseUrl(): string {
    const laboratoryConfig = this.appConfig.laboratory;
    if (!laboratoryConfig) {
      console.warn('Laboratory config not found, using default context');
      return '/laboratorio';
    }
    const baseUrl = laboratoryConfig.baseUrl || '';
    return `${baseUrl}${laboratoryConfig.context}`;
  }

  getObservations(
    params?: ObservationsQueryParams
  ): Observable<ObservationsPaginatedResult> {
    const queryParams = buildObservationsQueryParams(params);
    const url = `${this.buildBaseUrl()}/${this.BASE_ENDPOINT}${queryParams}`;

    return this.httpClient
      .get<LaboratoryObservationsResponse>(url, this.httpOptions)
      .pipe(
        retry({ count: 2, delay: 200 }),
        map((response) => ({
          observations: response.observations,
          pagination: {
            page: response.pagination.page,
            size: response.pagination.size,
            total: response.pagination.totalElements,
            totalPages: response.pagination.totalPages
          }
        })),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getObservations', error)
        )
      );
  }

  updateObservationStatus(
    observationId: string,
    statusId: string
  ): Observable<void> {
    const request: UpdateStatusRequest = { statusId };
    const url = `${this.buildBaseUrl()}/${this.BASE_ENDPOINT}/${observationId}`;

    return this.httpClient
      .patch<void>(url, request, this.httpOptions)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('updateObservationStatus', error)
        )
      );
  }

  getObservationHistory(
    observationId: string
  ): Observable<ReprocessHistoryDTO[]> {
    const url = `${this.buildBaseUrl()}/${this.BASE_ENDPOINT}/${observationId}/history`;

    return this.httpClient
      .get<ReprocessHistoryDTO[]>(url, this.httpOptions)
      .pipe(
        retry({ count: 2, delay: 200 }),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getObservationHistory', error)
        )
      );
  }

  addObservationHistory(
    observationId: string,
    observationText: string,
    lgCode?: string
  ): Observable<void> {
    const request: AddHistoryRequest = { observationText, lgCode };
    const url = `${this.buildBaseUrl()}/${this.BASE_ENDPOINT}/${observationId}/history`;

    return this.httpClient
      .post<void>(url, request, this.httpOptions)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('addObservationHistory', error)
        )
      );
  }

  exportObservations(params?: ObservationsQueryParams): Observable<Blob> {
    const queryParams = buildObservationsQueryParams(params, true);
    const url = `${this.buildBaseUrl()}/${this.BASE_ENDPOINT}/export${queryParams}`;

    return this.httpClient
      .get(url, { responseType: 'blob' })
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('exportObservations', error)
        )
      );
  }

  /**
   * Get observation metrics.
   */
  getMetrics(
    params?: ObservationMetricsParams
  ): Observable<ObservationMetrics> {
    const queryParams = buildMetricsQueryParams(params);
    const url = `${this.buildBaseUrl()}/${this.BASE_ENDPOINT}/metrics${queryParams}`;

    return this.httpClient
      .get<ObservationMetricsResponse>(url, this.httpOptions)
      .pipe(
        retry({ count: 2, delay: 200 }),
        map((response) => mapMetricsResponse(response)),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('getMetrics', error)
        )
      );
  }

  /**
   * Auto-refreshing stream of observation metrics.
   * @param params - Filter params for metrics
   * @param intervalMs - Refresh interval in milliseconds (default: 60000)
   */
  getMetricsStream(
    params?: ObservationMetricsParams,
    intervalMs = 60000
  ): Observable<ObservationMetrics> {
    return interval(intervalMs).pipe(
      startWith(0),
      switchMap(() => this.getMetrics(params))
    );
  }

}
